import React from 'react';
import { X, User, Mail, Phone, MapPin, Calendar, Briefcase, Globe, Mars, Venus, Transgender } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../CommonLoader';
import { getFileURL } from '../../utils/fileURL';
import MasterLabel from '../common/MasterLabel';

const PatientProfileModal = ({ isOpen, onClose, patient, loading }) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        let date;
        const str = String(dateString).trim();
        if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) {
            date = new Date(str);
        } else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) {
            const parts = str.split(/[/-]/);
            date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        } else {
            date = new Date(str);
        }
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const calculateAge = (dob) => {
        if (!dob) return '-';
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="patient-profile-modal"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="patient-profile-modal-content"
                    >
                        {/* Header */}
                        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white px-6 py-4 flex justify-between items-center shadow-md" data-testid="patient-profile-modal-header">
                            <h2 className="text-xl sm:text-2xl font-bold" data-testid="patient-profile-modal-title">
                                {t('patientProfileModal.title', 'Patient Profile')}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                                aria-label={t('patientProfileModal.close', 'Close')}
                                data-testid="patient-profile-modal-close-button"
                            >
                                <X className="w-6 h-6" data-testid="patient-profile-modal-close-icon" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 custom-scrollbar" data-testid="patient-profile-modal-body">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="patient-profile-modal-loading">
                                    <CommonLoader />
                                    <span className="text-xl text-[#4ABDBD] font-semibold mt-4" data-testid="patient-profile-modal-loading-text">
                                        {t('patientProfileModal.loading', 'Loading patient details...')}
                                    </span>
                                </div>
                            ) : patient ? (
                                <div className="space-y-6" data-testid="patient-profile-modal-data">
                                    {/* Personal Information Card */}
                                    <div className="bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-2xl p-6 shadow-sm" data-testid="patient-profile-basic-info-section">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-2 bg-[#4ABDBD] rounded-lg">
                                                <User className="text-white w-5 h-5" data-testid="patient-profile-basic-info-icon" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800" data-testid="patient-profile-basic-info-title">
                                                {t('patientProfileModal.basic_information', 'Basic Information')}
                                            </h3>
                                        </div>

                                        <div className="flex flex-col md:flex-row gap-6 items-start">
                                            {/* Avatar */}
                                            <div data-testid="patient-profile-modal-avatar-container" className="shrink-0">
                                                {patient.image ? (
                                                    <img
                                                        data-testid="patient-profile-modal-avatar-image"
                                                        src={getFileURL(patient.image)}
                                                        alt={`${patient.name} ${patient.surname}`}
                                                        className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                                                    />
                                                ) : (
                                                    <div data-testid="patient-profile-modal-avatar-placeholder" className="w-32 h-32 rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] flex items-center justify-center border-4 border-white shadow-lg">
                                                        <span className="text-white text-4xl font-bold">
                                                            {patient.name?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Details Grid */}
                                            <div data-testid="patient-profile-modal-details-grid" className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div data-testid="patient-profile-modal-full-name-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-full-name-label" className="text-xs font-semibold text-gray-500 uppercase">
                                                        {t('patientProfileModal.full_name', 'Full Name')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-full-name-text" className="text-base font-bold text-gray-900">
                                                        {patient.name} {patient.surname}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-email-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-email-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('patientProfileModal.email', 'Email')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-email-text" className="text-base font-medium text-gray-900 break-all">
                                                        {patient.email || '-'}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-phone-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-phone-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Phone className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('patientProfileModal.phone', 'Phone')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-phone-text" className="text-base font-medium text-gray-900">
                                                        {patient.phone || '-'}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-gender-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-gender-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        {patient.gender?.toLowerCase() === 'male' && <Mars className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                                                        {patient.gender?.toLowerCase() === 'female' && <Venus className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                                                        {patient.gender?.toLowerCase() === 'other' && <Transgender className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                                                        {t('patientProfileModal.gender', 'Gender')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-gender-text" className="text-base font-medium text-gray-900 capitalize">
                                                        {patient.gender ? t(`common.gender.${patient.gender.toLowerCase()}`, patient.gender) : '-'}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-dob-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-dob-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Calendar className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('patientProfileModal.dob', 'Date of Birth')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-dob-text" className="text-base font-medium text-gray-900">
                                                        {formatDate(patient.dob)}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-age-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-age-label" className="text-xs font-semibold text-gray-500 uppercase">
                                                        {t('patientProfileModal.age', 'Age')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-age-text" className="text-base font-medium text-gray-900">
                                                        {calculateAge(patient.dob)}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-occupation-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-occupation-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Briefcase className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('patientProfileModal.occupation', 'Occupation')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-occupation-text" className="text-base font-medium text-gray-900">
                                                        {patient.occupation || '-'}
                                                    </p>
                                                </div>

                                                <div data-testid="patient-profile-modal-created-at-field" className="space-y-1">
                                                    <p data-testid="patient-profile-modal-created-at-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Calendar className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('patientProfileModal.created_at', 'Created At')}
                                                    </p>
                                                    <p data-testid="patient-profile-modal-created-at-text" className="text-base font-medium text-gray-900">
                                                        {formatDate(patient.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact Information Card */}
                                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-6 shadow-sm" data-testid="patient-profile-contact-info-section">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-2 bg-blue-500 rounded-lg">
                                                <MapPin className="text-white w-5 h-5" data-testid="patient-profile-contact-info-icon" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800" data-testid="patient-profile-contact-info-title">
                                                {t('patientProfileModal.contact_information', 'Contact Information')}
                                            </h3>
                                        </div>

                                        <div data-testid="patient-profile-contact-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div data-testid="patient-profile-modal-address-field" className="space-y-1">
                                                <p data-testid="patient-profile-modal-address-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                    <MapPin className="w-3.5 h-3.5 text-blue-500" />
                                                    {t('patientProfileModal.address', 'Address')}
                                                </p>
                                                <p data-testid="patient-profile-modal-address-text" className="text-base font-medium text-gray-900">
                                                    {patient.address || '-'}
                                                </p>
                                            </div>

                                            <div data-testid="patient-profile-modal-additional-field" className="space-y-1">
                                                <p data-testid="patient-profile-modal-additional-label" className="text-xs font-semibold text-gray-500 uppercase">
                                                    {t('patientProfileModal.additional', 'Additional Info')}
                                                </p>
                                                <p data-testid="patient-profile-modal-additional-text" className="text-base font-medium text-gray-900">
                                                    {patient.additional || '-'}
                                                </p>
                                            </div>

                                            <div data-testid="patient-profile-modal-country-field" className="space-y-1">
                                                <p data-testid="patient-profile-modal-country-label" className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                    <Globe className="w-3.5 h-3.5 text-blue-500" />
                                                    {t('patientProfileModal.country', 'Country')}
                                                </p>
                                                <p data-testid="patient-profile-modal-country-text" className="text-base font-medium text-gray-900 capitalize">
                                                    <MasterLabel type="masterCountry" id={patient.country?.countryId} field="countryName" fallback={patient.country?.countryName || patient.country || '-'} />
                                                </p>
                                            </div>

                                            <div data-testid="patient-profile-modal-city-field" className="space-y-1">
                                                <p data-testid="patient-profile-modal-city-label" className="text-xs font-semibold text-gray-500 uppercase">
                                                    {t('patientProfileModal.city', 'City')}
                                                </p>
                                                <p data-testid="patient-profile-modal-city-text" className="text-base font-medium text-gray-900">
                                                    {patient.city || '-'}
                                                </p>
                                            </div>

                                            <div data-testid="patient-profile-modal-zipcode-field" className="space-y-1">
                                                <p data-testid="patient-profile-modal-zipcode-label" className="text-xs font-semibold text-gray-500 uppercase">
                                                    {t('patientProfileModal.zipcode', 'Zip Code')}
                                                </p>
                                                <p data-testid="patient-profile-modal-zipcode-text" className="text-base font-medium text-gray-900">
                                                    {patient.zipCode || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="patient-profile-modal-no-data">
                                    <p className="text-gray-500 text-lg" data-testid="patient-profile-modal-no-data-text">
                                        {t('patientProfileModal.no_data', 'No patient data available')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PatientProfileModal;
